import { Vector2 } from '@technology/web3d';
import {
  SceneProjection,
  TileMapSettings,
} from '@technology/web3d-plugin-tilemap/dist/esnext/common';
import { getCsibWithoutGeoid } from '@technology/web3d-plugin-tilemap/dist/esnext/tgl/csdManagementUtils';
import {
  initTgl,
  getTglTransformFunction,
  TglTransformFunction,
} from '@technology/web3d-plugin-tilemap/dist/esnext/tgl/tgl';
import { getLogger } from '@trimble/logging';
import { lonLatToWebMercatorMeters } from '@trimble/utilities';
import { TileMapOptions, TileMapProjection } from './tile-map-provider.types';
import { defaultMercatorProjection } from './mercator-projection';

const MAX_SHRINK_ITERATIONS = 10;
const MIN_HALF_EXTENT_METERS = 50;
const OUT_OF_BOUNDS_ERROR_CODE = -108;

// Temporary utility function to convert lat/lon to TMS tile coordinates
// The TileMapPlugin currently has this function, but it's marked as private.
// Once the plugin is updated, this can be removed.
export function getTileCoordsFromLatLon(
  latDeg: number,
  lonDeg: number,
  zoom: number
): Vector2 {
  // eslint-disable-next-line no-magic-numbers
  const latRad = (latDeg * Math.PI) / 180;
  // eslint-disable-next-line no-magic-numbers
  const n = Math.pow(2, zoom);
  // eslint-disable-next-line no-magic-numbers
  const xCoord = Math.floor(((lonDeg + 180.0) / 360.0) * n);
  const yCoord =
    n -
    // eslint-disable-next-line no-magic-numbers
    Math.floor(((1.0 - Math.asinh(Math.tan(latRad)) / Math.PI) / 2.0) * n) -
    1;
  return new Vector2(xCoord, yCoord);
}

export async function getProjectionOptions(
  p: TileMapProjection
): Promise<SceneProjection> {
  return 'csib' in p
    ? await getSceneProjectionFromCsib(p.csib, p.geoDataFiles)
    : p.projectionOptions;
}

function decodeCsibToArrayBuffer(csib64: string): ArrayBuffer {
  return Uint8Array.from(atob(csib64), (c) => c.charCodeAt(0)).buffer;
}

export interface LonLatToSceneConverter {
  /** WGS-84 lon/lat (degrees) -> local scene easting/northing, or null if TGL cannot resolve it. */
  lonLatToScene(lon: number, lat: number): Vector2 | null;
  /** Local scene easting/northing -> WGS-84 lon/lat (degrees), or null if TGL cannot resolve it. */
  sceneToLonLat(x: number, y: number): { lon: number; lat: number } | null;
}

/**
 * Builds a client-side WGS-84 lon/lat <-> local scene (easting/northing) converter
 * for the coordinate system described by the supplied base64 CSIB, using TGL. This
 * mirrors the transform the features/tilemap plugins apply internally, so it needs
 * no Coordinates API round-trip.
 */
export async function createLonLatToSceneConverter(
  csib64: string,
  geoDataFiles: Map<string, ArrayBuffer> = new Map()
): Promise<LonLatToSceneConverter> {
  await initTgl();
  const csibWithoutGeoid = await getCsibWithoutGeoid(
    decodeCsibToArrayBuffer(csib64),
    geoDataFiles
  );
  const tglTransform = getTglTransformFunction(csibWithoutGeoid, geoDataFiles);
  return {
    lonLatToScene: (lon, lat) => {
      const merc = lonLatToWebMercatorMeters({ lon, lat });
      const res = tglTransform(merc.x, merc.y, false);
      return res.errorCode === 0 ? new Vector2(res.x, res.y) : null;
    },
    sceneToLonLat: (x, y) => {
      const res = tglTransform(x, y, true);
      return res.errorCode === 0 ? { lon: res.x, lat: res.y } : null;
    },
  };
}

export interface CsibLonLat {
  readonly lon: number;
  readonly lat: number;
}

/**
 * Converts CSIB easting/northing to WGS84 lon/lat via TGL.
 */
export async function csibEastNorthToLonLat(
  east: number,
  north: number,
  csib64: string,
  geoDataFiles: Map<string, ArrayBuffer> = new Map()
): Promise<CsibLonLat | undefined> {
  try {
    await initTgl();

    const csibWithoutGeoid = await getCsibWithoutGeoid(
      decodeCsibToArrayBuffer(csib64),
      geoDataFiles
    );
    const tglTransform = getTglTransformFunction(
      csibWithoutGeoid,
      geoDataFiles
    );
    const result = tglTransform(east, north, true);

    if (result.errorCode !== 0) {
      getLogger().error('TGL transformation error', {
        errorCode: result.errorCode,
        east,
        north,
      });
      return undefined;
    }

    return { lon: result.x, lat: result.y };
  } catch (error) {
    getLogger().error('Failed to convert CSIB east/north to lon/lat', error);
    return undefined;
  }
}

export async function getSceneProjectionFromCsib(
  csib64: string,
  geoDataFiles?: Map<string, ArrayBuffer>
): Promise<SceneProjection> {
  if (!csib64 || csib64.trim() === '') {
    return {
      proj4def: defaultMercatorProjection.proj4def,
    };
  }

  const csibWithoutGeoid = await getCsibWithoutGeoid(
    decodeCsibToArrayBuffer(csib64),
    geoDataFiles
  );

  return {
    tglDef: csibWithoutGeoid,
  };
}

type TransformFailure = {
  readonly reason: 'nonRecoverableError' | 'outOfBoundsExhausted';
  readonly attempts: number;
  readonly lastSwError: number;
  readonly lastNeError: number;
  readonly finalHalfExtent: { readonly x: number; readonly y: number };
};

function tryTransformBounds(
  center: Vector2,
  initialHalfExtent: Vector2,
  tglTransform: TglTransformFunction
): [Vector2, Vector2] | TransformFailure {
  let halfExtent = new Vector2(initialHalfExtent.x, initialHalfExtent.y);

  for (let i = 0; i < MAX_SHRINK_ITERATIONS; i++) {
    const swLonLat = tglTransform(
      center.x - halfExtent.x,
      center.y - halfExtent.y,
      true
    );
    const neLonLat = tglTransform(
      center.x + halfExtent.x,
      center.y + halfExtent.y,
      true
    );

    if (swLonLat.errorCode === 0 && neLonLat.errorCode === 0) {
      const cornerA = lonLatToWebMercatorMeters({
        lon: swLonLat.x,
        lat: swLonLat.y,
      });
      const cornerB = lonLatToWebMercatorMeters({
        lon: neLonLat.x,
        lat: neLonLat.y,
      });

      // CSIB axes can be west-positive / south-positive (e.g. SA Lo).
      // Tile load limits must be min/max in Web Mercator.
      return [
        new Vector2(
          Math.min(cornerA.x, cornerB.x),
          Math.min(cornerA.y, cornerB.y)
        ),
        new Vector2(
          Math.max(cornerA.x, cornerB.x),
          Math.max(cornerA.y, cornerB.y)
        ),
      ];
    }

    const hasOutOfBoundsError =
      swLonLat.errorCode === OUT_OF_BOUNDS_ERROR_CODE ||
      neLonLat.errorCode === OUT_OF_BOUNDS_ERROR_CODE;

    if (!hasOutOfBoundsError) {
      return {
        reason: 'nonRecoverableError',
        attempts: i + 1,
        lastSwError: swLonLat.errorCode,
        lastNeError: neLonLat.errorCode,
        finalHalfExtent: { x: halfExtent.x, y: halfExtent.y },
      };
    }

    getLogger().debug('TGL out-of-bounds, shrinking extent', {
      attempt: i + 1,
      swError: swLonLat.errorCode,
      neError: neLonLat.errorCode,
      halfExtent: { x: halfExtent.x, y: halfExtent.y },
    });

    // eslint-disable-next-line no-magic-numbers
    halfExtent = new Vector2(halfExtent.x / 2, halfExtent.y / 2);

    if (
      halfExtent.x < MIN_HALF_EXTENT_METERS ||
      halfExtent.y < MIN_HALF_EXTENT_METERS
    ) {
      return {
        reason: 'outOfBoundsExhausted',
        attempts: i + 1,
        lastSwError: swLonLat.errorCode,
        lastNeError: neLonLat.errorCode,
        finalHalfExtent: { x: halfExtent.x, y: halfExtent.y },
      };
    }
  }

  return {
    reason: 'outOfBoundsExhausted',
    attempts: MAX_SHRINK_ITERATIONS,
    lastSwError: OUT_OF_BOUNDS_ERROR_CODE,
    lastNeError: OUT_OF_BOUNDS_ERROR_CODE,
    finalHalfExtent: { x: halfExtent.x, y: halfExtent.y },
  };
}

/**
 * Converts bounds from CSIB projection to Web Mercator using TGL transformations.
 * @param bounds The bounds in CSIB projection [southwest, northeast]
 * @param csib64 The base64-encoded CSIB
 * @param geoDataFiles Map of geodata files needed for TGL transformations
 * @returns The bounds converted to Web Mercator, or undefined if conversion fails
 */
export async function convertBoundsToWebMercatorUsingCsib(
  bounds: [Vector2, Vector2],
  csib64: string,
  geoDataFiles: Map<string, ArrayBuffer>
): Promise<[Vector2, Vector2] | undefined> {
  try {
    await initTgl();

    const csibWithoutGeoid = await getCsibWithoutGeoid(
      decodeCsibToArrayBuffer(csib64),
      geoDataFiles
    );
    const tglTransform = getTglTransformFunction(
      csibWithoutGeoid,
      geoDataFiles
    );

    const [sw, ne] = bounds;
    // eslint-disable-next-line no-magic-numbers
    const center = new Vector2((sw.x + ne.x) / 2, (sw.y + ne.y) / 2);
    const initialHalfExtent = new Vector2(
      // eslint-disable-next-line no-magic-numbers
      Math.abs(ne.x - sw.x) / 2,
      // eslint-disable-next-line no-magic-numbers
      Math.abs(ne.y - sw.y) / 2
    );

    const result = tryTransformBounds(center, initialHalfExtent, tglTransform);

    if (Array.isArray(result)) {
      return result;
    }

    getLogger().error('TGL transformation error', {
      ...result,
      initialHalfExtent: { x: initialHalfExtent.x, y: initialHalfExtent.y },
    });
    return undefined;
  } catch (error) {
    getLogger().error('Failed to convert bounds using CSIB', error);
    return undefined;
  }
}

/**
 * Converts TileMapOptions to TileMapSettings with appropriate defaults.
 * This method handles the conversion of projection options and applies default values
 * for attribution, image size, and max zoom level when not specified.
 * @param tileOptions The tile map options to convert.
 * @returns The converted TileMapSettings.
 */
export function convertTileMapOptionsToTileMapSettings(
  tileOptions: TileMapOptions
): TileMapSettings {
  if (!('projectionOptions' in tileOptions.projection)) {
    throw new Error('TileMapOptions Projection must have projectionOptions');
  }

  return {
    modelId: tileOptions.modelId,
    baseUrl: tileOptions.baseUrl,
    projection: tileOptions.projection.projectionOptions,
    attribution:
      tileOptions.attribution ??
      'Map tiles &copy <a href="https://maps.trimble.com">Trimble Maps</a>',
    options: {
      // eslint-disable-next-line no-magic-numbers
      imageSize: tileOptions.imageSize ?? 256,
      // eslint-disable-next-line no-magic-numbers
      minZoomLevel: tileOptions.minZoomLevel ?? 8,
      // eslint-disable-next-line no-magic-numbers
      maxZoomLevel: tileOptions.maxZoomLevel ?? 20,
      transparent: tileOptions.transparent,
      renderBelow: true,
    },
  };
}
